import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDownIcon, CheckIcon, SearchIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { norm } from '@/lib/text'

export interface ComboOption {
  value: string
  label: string
  /** Nível de indentação (0 = pai/raiz, 1 = filho…). */
  depth?: number
  /** Deixa o item em negrito (ex.: categoria-pai). */
  bold?: boolean
  /** Termos extras para casar na busca além do label. */
  keywords?: string
  /** Cabeçalho de grupo: rótulo não-clicável, pulado na navegação e na busca. */
  header?: boolean
}

// Posição do menu flutuante, em coordenadas do BLOCO CONTINENTE do portal.
interface MenuPos {
  left: number
  width: number
  /** distância do topo do bloco continente quando abre PARA BAIXO */
  top?: number
  /** distância do fundo do bloco continente quando abre PARA CIMA */
  bottom?: number
  /** altura máxima da LISTA, respeitando o espaço disponível */
  listMax: number
}

// O portal precisa cair DENTRO do DialogContent quando o combobox está num dialog:
// o Dialog do Radix é modal e só libera foco (FocusScope) e rolagem (RemoveScroll)
// para elementos que estejam na sua subárvore do DOM. Portalar no body faria a lista
// não rolar e o campo de busca perder o foco. Fora de um dialog, o alvo é o body.
const dialogHost = (el: Element | null) =>
  (el?.closest('[data-slot="dialog-content"]') as HTMLElement | null) ?? null

// Select com BUSCA: trigger + campo de digitação que filtra a lista. O menu é
// renderizado num PORTAL com position: fixed — assim ele FLUTUA sobre tudo e nunca
// é cortado pelo conteúdo que rola por dentro do dialog. Sem cmdk/popover.
export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Selecione',
  searchPlaceholder = 'Buscar…',
  emptyText = 'Nada encontrado',
  disabled,
  className,
  'aria-label': ariaLabel,
}: {
  options: ComboOption[]
  value: string | null | undefined
  onChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  /** Nome acessível quando não há <label> associado (ex.: filtros). */
  'aria-label'?: string
}) {
  const listboxId = useId()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Item destacado para navegação por teclado (↑ ↓ + Enter).
  const [active, setActive] = useState(0)
  const [pos, setPos] = useState<MenuPos | null>(null)
  const [host, setHost] = useState<HTMLElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const selected = options.find((o) => o.value === value)

  const filtered = useMemo(() => {
    const q = norm(query.trim())
    if (!q) return options
    // Busca por SUBSTRING (qualquer parte) e sem acento: "al" casa Álcool/Alimentação,
    // "cao" casa Alimentação. Cada palavra digitada precisa aparecer (ordem livre).
    const terms = q.split(/\s+/)
    return options.filter((o) => {
      if (o.header) return false // cabeçalhos somem durante a busca
      const hay = norm(`${o.label} ${o.keywords ?? ''}`)
      return terms.every((t) => hay.includes(t))
    })
  }, [options, query])

  // Próximo índice SELECIONÁVEL (pula cabeçalhos) a partir de `from` no sentido `dir`.
  const nextSelectable = useCallback(
    (from: number, dir: 1 | -1) => {
      let i = from
      for (let k = 0; k < filtered.length; k++) {
        i += dir
        if (i < 0 || i >= filtered.length) return from
        if (!filtered[i]?.header) return i
      }
      return from
    },
    [filtered],
  )
  const firstSelectable = useCallback(() => filtered.findIndex((o) => !o.header), [filtered])
  const lastSelectable = useCallback(() => {
    for (let i = filtered.length - 1; i >= 0; i--) if (!filtered[i].header) return i
    return 0
  }, [filtered])

  // Calcula onde o menu cabe: abre para baixo se houver espaço, senão para cima.
  // O espaço disponível é sempre o da VIEWPORT (o menu pode transbordar o dialog),
  // mas as coordenadas saem relativas ao bloco continente do portal.
  const place = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const margin = 8
    const below = window.innerHeight - r.bottom - margin
    const above = r.top - margin
    const searchBar = 48 // altura aproximada da barra de busca acima da lista
    const up = below < 220 && above > below
    const space = (up ? above : below) - searchBar

    // Dentro de um dialog o portal vira filho do DialogContent, que tem transform
    // (translate -50%) e por isso passa a ser o bloco continente do position: fixed.
    // As medidas do getBoundingClientRect são de viewport, então descontamos a
    // origem da CAIXA DE PADDING do host (rect + borda). No body, a origem é 0.
    const h = dialogHost(el)
    setHost(h)
    let ox = 0
    let oy = 0
    let ob = window.innerHeight
    if (h) {
      const hr = h.getBoundingClientRect()
      ox = hr.left + h.clientLeft
      oy = hr.top + h.clientTop
      ob = oy + h.clientHeight
    }

    // O menu NÃO fica preso à largura do trigger: cresce até ~280px (ou a largura
    // do trigger, se maior) para não cortar nomes longos em filtros estreitos, mas
    // sempre cabendo na viewport. Se estourar a borda direita, desloca para a esquerda.
    const width = Math.min(Math.max(r.width, 280), window.innerWidth - 2 * margin)
    let vpLeft = r.left
    if (vpLeft + width > window.innerWidth - margin) vpLeft = window.innerWidth - margin - width
    if (vpLeft < margin) vpLeft = margin

    setPos({
      left: vpLeft - ox,
      width,
      top: up ? undefined : r.bottom + 4 - oy,
      bottom: up ? ob - r.top + 4 : undefined,
      listMax: Math.max(120, Math.min(300, space)),
    })
  }, [])

  // Reposiciona ao abrir e sempre que rolar/redimensionar (o dialog rola por dentro,
  // por isso o listener de scroll usa capture para pegar qualquer container).
  useLayoutEffect(() => {
    if (open) place()
  }, [open, place])
  useEffect(() => {
    if (!open) return
    const on = () => place()
    window.addEventListener('scroll', on, true)
    window.addEventListener('resize', on)
    return () => {
      window.removeEventListener('scroll', on, true)
      window.removeEventListener('resize', on)
    }
  }, [open, place])

  // Fecha ao clicar fora do trigger E do menu (o menu vive no portal, fora da raiz).
  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      const t = e.target as Node
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // Ao abrir: limpa a busca, foca o campo e destaca o item já selecionado
  // (ou o primeiro selecionável, pulando cabeçalhos de grupo).
  useEffect(() => {
    if (!open) return
    setQuery('')
    const i = options.findIndex((o) => o.value === value)
    setActive(i >= 0 && !options[i].header ? i : options.findIndex((o) => !o.header))
    const t = setTimeout(() => inputRef.current?.focus(), 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Ao digitar, a lista muda: volta o destaque para o primeiro resultado selecionável.
  useEffect(() => {
    setActive(firstSelectable())
  }, [query, firstSelectable])

  // Mantém o item destacado visível ao navegar com as setas.
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  function pick(v: string) {
    onChange(v)
    setOpen(false)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => nextSelectable(i, 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => nextSelectable(i, -1))
    } else if (e.key === 'Home') {
      e.preventDefault()
      setActive(firstSelectable())
    } else if (e.key === 'End') {
      e.preventDefault()
      setActive(lastSelectable())
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const o = filtered[active]
      if (o && !o.header) pick(o.value)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
    }
  }

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          // Fechado: ↓ ou Enter abre a lista (fluxo só de teclado).
          if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className={cn(
          'border-input flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow]',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          'disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30',
        )}
      >
        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              left: pos.left,
              width: pos.width,
              top: pos.top,
              bottom: pos.bottom,
              // Quando o portal cai no body, o Dialog (Radix) põe pointer-events:none
              // nele — precisa reativar o clique explicitamente aqui.
              pointerEvents: 'auto',
            }}
            className="bg-popover text-popover-foreground z-[60] overflow-hidden rounded-md border shadow-md"
          >
            <div className="flex items-center gap-2 border-b px-2">
              <SearchIcon className="text-muted-foreground size-4 shrink-0" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-9 border-0 px-0 shadow-none focus-visible:ring-0"
                onKeyDown={onKeyDown}
                aria-controls={listboxId}
                aria-activedescendant={filtered.length > 0 ? `${listboxId}-opt-${active}` : undefined}
              />
            </div>
            <div
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel ?? placeholder}
              style={{ maxHeight: pos.listMax }}
              className="overflow-y-auto p-1"
            >
              {filtered.length === 0 ? (
                <div className="text-muted-foreground p-3 text-center text-sm">{emptyText}</div>
              ) : (
                filtered.map((o, i) =>
                  o.header ? (
                    <div
                      key={o.value}
                      className="text-muted-foreground px-2 pt-2.5 pb-1 text-[11px] font-semibold tracking-wide uppercase select-none first:pt-1"
                    >
                      {o.label}
                    </div>
                  ) : (
                    <button
                      key={o.value}
                      id={`${listboxId}-opt-${i}`}
                      type="button"
                      role="option"
                      aria-selected={value === o.value}
                      data-idx={i}
                      onClick={() => pick(o.value)}
                      onMouseEnter={() => setActive(i)}
                      style={{ paddingLeft: o.depth ? 8 + o.depth * 16 : undefined }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
                        // destaque do item ativo — o mesmo para mouse e teclado
                        i === active && 'bg-accent text-accent-foreground',
                        o.bold && 'font-medium',
                      )}
                    >
                      <CheckIcon className={cn('size-4 shrink-0', value === o.value ? 'opacity-100' : 'opacity-0')} />
                      <span className="truncate">{o.label}</span>
                    </button>
                  ),
                )
              )}
            </div>
          </div>,
          host ?? document.body,
        )}
    </div>
  )
}
